import { useState, useEffect } from "react";
import { useFetch } from "../../hooks/useFetch";
import { getProjects, getHackathons, getCertificates } from "../../services/firestore";
import "./Hero.css";

const ROLES = [
  "Software Developer",
  "Web Developer",
  "App Developer",
  "Game Developer",
  "Hackathon Enthusiast",
  "Open Source Contributor",
];

export default function Hero({ owner, loading = false }) {
  const [roleIdx, setRoleIdx]   = useState(0);
  const [typed, setTyped]       = useState("");
  const [deleting, setDeleting] = useState(false);

  const { data: projects }    = useFetch(getProjects, []);
  const { data: hackathons }  = useFetch(getHackathons, []);
  const { data: certificates} = useFetch(getCertificates, []);

  useEffect(() => {
    if (loading) {
      return undefined;
    }

    const current = ROLES[roleIdx];
    let timeout;
    if (!deleting && typed.length < current.length) {
      timeout = setTimeout(() => setTyped(current.slice(0, typed.length + 1)), 60);
    } else if (!deleting && typed.length === current.length) {
      timeout = setTimeout(() => setDeleting(true), 2000);
    } else if (deleting && typed.length > 0) {
      timeout = setTimeout(() => setTyped(current.slice(0, typed.length - 1)), 30);
    } else if (deleting && typed.length === 0) {
      setDeleting(false);
      setRoleIdx((i) => (i + 1) % ROLES.length);
    }
    return () => clearTimeout(timeout);
  }, [typed, deleting, roleIdx, loading]);

  if (loading) {
    return (
      <section id="hero" className="hero hero--loading" aria-busy="true" aria-live="polite">
        <div className="container hero__skeleton-wrap fade-up">
          <div className="hero__skeleton-line skeleton hero__skeleton-line--eyebrow" />
          <div className="hero__skeleton-line skeleton hero__skeleton-line--name" />
          <div className="hero__skeleton-line skeleton hero__skeleton-line--role" />
          <div className="hero__skeleton-line skeleton hero__skeleton-line--bio" />
          <div className="hero__skeleton-line skeleton hero__skeleton-line--bio short" />
          <div className="hero__skeleton-actions">
            <div className="hero__skeleton-btn skeleton" />
            <div className="hero__skeleton-btn skeleton hero__skeleton-btn--ghost" />
          </div>
        </div>
      </section>
    );
  }

  const stats = [
    { label: "projects built",       value: projects?.length },
    { label: "hackathons entered",   value: hackathons?.length },
    { label: "certifications",       value: certificates?.length },
  ];

  const hasResume = Boolean(owner?.resume_storage_url || owner?.resume_url);

  return (
    <section id="hero" className="hero">
      <div className="container hero__inner">
        <p className="hero__eyebrow fade-up">{"// hi, my name is"}</p>

        <h1 className="hero__name fade-up" style={{ animationDelay: "0.05s" }}>
          {owner?.name || "Your Name"}
        </h1>

        <p className="hero__role fade-up" style={{ animationDelay: "0.1s" }}>
          <span className="hero__role-prefix">$</span>
          {typed}
          <span className="hero__cursor" aria-hidden="true" />
        </p>

        <p className="hero__bio fade-up" style={{ animationDelay: "0.15s" }}>
          {owner?.bio || "Building cool things on the web."}
        </p>

        <div className="hero__actions fade-up" style={{ animationDelay: "0.2s" }}>
          <a href="#projects" className="btn btn-primary">View projects</a>
          {hasResume && (
            <a
              href={owner.resume_storage_url || owner.resume_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
            >
              Résumé <span aria-hidden="true">↗</span>
            </a>
          )}
          <a href="#contact" className="hero__link-quiet">
            get in touch <span aria-hidden="true">↓</span>
          </a>
        </div>

        <dl className="hero__stats fade-up" style={{ animationDelay: "0.3s" }}>
          {stats.map(({ label, value }) => (
            <div className="hero__stat" key={label}>
              <dt className="hero__stat-label">{label}</dt>
              <dd className="hero__stat-value">{value ?? "—"}</dd>
            </div>
          ))}
        </dl>
      </div>

      <a href="#skills" className="hero__scroll-hint" aria-label="scroll to content">
        <span>scroll</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </a>
    </section>
  );
}
